/**
 * The wait between two signing levels, borrowed whole from the create-request
 * flow's own `SignerDelaySlot` — same stem, same dashed "Add delay" pill, same
 * − / + steppers.
 *
 * Only the wiring differs: that component drives redux signers, while a batch
 * holds its roles in local state, so the markup and styles are shared and the
 * state is not. An absolute "wait until" date is left out here — a batch sets
 * its send time once, on the Review step, rather than per level.
 */

import { Box, Button, IconButton, Tooltip } from "@mui/material";
import approversAndSignersStyles from "screens/SignRequest/ApproversAndSigners.styles";
import {
  ClockGlyph,
  CloseGlyph,
} from "screens/SignRequest/components/RecipientsIcons";

const styles = approversAndSignersStyles;

/** Days the design's − / + steppers can reach, as the normal flow bounds them. */
const MIN_DELAY_DAYS = 1;
const MAX_DELAY_DAYS = 99;

interface Props {
  days: number;
  hours: number;
  /** The plan gate — the pill is offered but locked when delayed routing is off. */
  disabled: boolean;
  tooltip: string;
  /** Which level waits, for the labels a screen reader reads out. */
  levelLabel: string;
  onChange: (days: number, hours: number) => void;
  onRemove: () => void;
}

const BulkDelaySlot = ({
  days,
  hours,
  disabled,
  tooltip,
  levelLabel,
  onChange,
  onRemove,
}: Props) => {
  const hasDelay = days > 0 || hours > 0;

  const stepDays = (delta: number) =>
    onChange(
      Math.min(MAX_DELAY_DAYS, Math.max(MIN_DELAY_DAYS, days + delta)),
      hours,
    );

  return (
    <Box sx={styles.delaySlot}>
      <Box sx={styles.delayStem} />
      {hasDelay ? (
        <Box sx={styles.delayPill}>
          <ClockGlyph sx={styles.delayClock} />
          <Box component="span" sx={styles.delayText}>
            Wait {days} day(s){hours ? ` ${hours} hour(s)` : ""} before next
            level
          </Box>
          <Box sx={styles.delaySteps}>
            <Button
              disableRipple
              aria-label={`Decrease the wait before ${levelLabel}`}
              onClick={() => stepDays(-1)}
              sx={styles.delayStep}
            >
              −
            </Button>
            <Button
              disableRipple
              aria-label={`Increase the wait before ${levelLabel}`}
              onClick={() => stepDays(1)}
              sx={styles.delayStep}
            >
              +
            </Button>
            <IconButton
              disableRipple
              aria-label={`Remove the wait before ${levelLabel}`}
              onClick={onRemove}
              sx={styles.delayRemove}
            >
              <CloseGlyph sx={styles.delayRemoveIcon} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Tooltip
          title={disabled ? tooltip : ""}
          arrow
          disableHoverListener={!disabled}
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Button
              disableRipple
              disabled={disabled}
              aria-label={`Add a wait before ${levelLabel}`}
              onClick={() => onChange(MIN_DELAY_DAYS, 0)}
              sx={styles.delayAdd}
            >
              <ClockGlyph sx={styles.delayAddIcon} />
              Add delay
            </Button>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default BulkDelaySlot;
